import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  X as XIcon,
  Clock as ClockIcon,
  User as UserIcon,
  Bank as BankIcon,
  Buildings as BuildingsIcon,
  Users as UsersIcon,
  ArrowSquareOut as ArrowSquareOutIcon,
  CurrencyDollar as CurrencyDollarIcon,
  File as FileIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { approveReservation, rejectReservation, getCommunityReservation } from '@/api/reservations';
import { getS3PresignedUrlFromPath } from '@/utils/s3';
import { useUser } from '@/hooks/v2/use-user';
import { toast } from '@/components/core/v2/toaster';
import { paths } from '@/paths';
import type { BlockedSlot, AmenityReservation } from '@/types/v2/reservation';
import { ProfessionalDialog } from '@/components/common';

interface ReservationDetailsDialogProps {
  open: boolean;
  reservation: BlockedSlot | null;
  onClose: () => void;
  onSuccess: () => void;
}

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'default',
  completed: 'info',
  no_show: 'error',
  waitlisted: 'info',
};

// ── Resident view ─────────────────────────────────────────────────────────────
// Shows only public, non-identifying information about any slot so residents
// can check when the amenity is available before making their own reservation.
function ResidentSlotDialog({
  open,
  slot,
  onClose,
}: {
  open: boolean;
  slot: BlockedSlot;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const startLocal = dayjs.utc(slot.start_time).local();
  const endLocal = dayjs.utc(slot.end_time).local();
  const durationMins = endLocal.diff(startLocal, 'minute');
  const hours = Math.floor(durationMins / 60);
  const mins = durationMins % 60;
  const durationLabel = hours > 0
    ? `${hours}h${mins > 0 ? ` ${mins}m` : ''}`
    : `${mins}m`;

  return (
    <ProfessionalDialog
      icon={<BuildingsIcon size={24} weight="duotone" />}
      maxWidth="xs"
      onClose={onClose}
      open={open}
      title={slot.amenity_name ?? 'Amenity'}
      subtitle={slot.is_mine ? 'Your reservation' : 'Reservation details'}
      actions={
        <Stack direction="row" spacing={1} justifyContent="flex-end" width="100%">
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2.5}>
        {/* Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={(slot.status as string).replace('_', ' ').toUpperCase()}
            size="small"
            color={statusColors[slot.status as string] ?? 'default'}
          />
        </Box>

        {/* Date / time */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <ClockIcon size={20} weight="duotone" style={{ marginTop: 2, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {startLocal.format('dddd, MMMM D, YYYY')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {startLocal.format('h:mm A')} – {endLocal.format('h:mm A')}
              <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.75 }}>
                ({durationLabel})
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Stack>
    </ProfessionalDialog>
  );
}

// ── Inline document list used by the manager dialog ───────────────────────────
function DialogDocumentsSection({
  documents,
}: {
  documents: AmenityReservation['documents'];
}): React.JSX.Element | null {
  const [presignedUrls, setPresignedUrls] = React.useState<Record<string, string>>({});
  const [loadingId, setLoadingId] = React.useState<string | null>(null);
  const [previewId, setPreviewId] = React.useState<string | null>(null);

  if (!documents?.length) return null;

  const isImage = (name?: string) => /\.(jpe?g|png|gif|webp)$/i.test(name ?? '');
  const isPdf = (name?: string) => /\.pdf$/i.test(name ?? '');

  const handleOpen = async (doc: NonNullable<AmenityReservation['documents']>[0]) => {
    if (presignedUrls[doc.id]) {
      setPreviewId(doc.id);
      return;
    }
    setLoadingId(doc.id);
    try {
      const url = await getS3PresignedUrlFromPath(doc.file_key);
      setPresignedUrls((prev) => ({ ...prev, [doc.id]: url }));
      setPreviewId(doc.id);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
        <FileIcon size={18} weight="duotone" />
        <Typography variant="subtitle2" fontWeight={600}>Submitted Documents</Typography>
      </Box>
      <Stack divider={<Divider />}>
        {documents.map((doc) => (
          <Box key={doc.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{doc.document_type}</Typography>
                {doc.file_name && (
                  <Typography variant="caption" color="text.secondary" noWrap>{doc.file_name}</Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1} flexShrink={0}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpen(doc)}
                  disabled={loadingId === doc.id}
                  sx={{ textTransform: 'none' }}
                >
                  {loadingId === doc.id ? 'Loading…' : previewId === doc.id ? 'Hide' : 'Preview'}
                </Button>
                {presignedUrls[doc.id] && (
                  <Button
                    size="small"
                    variant="text"
                    href={presignedUrls[doc.id]}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<ArrowSquareOutIcon size={14} />}
                    sx={{ textTransform: 'none' }}
                  >
                    Open
                  </Button>
                )}
              </Stack>
            </Box>
            {previewId === doc.id && presignedUrls[doc.id] && (
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                {isImage(doc.file_name) ? (
                  <Box
                    component="img"
                    src={presignedUrls[doc.id]}
                    alt={doc.file_name ?? doc.document_type}
                    sx={{ width: '100%', maxHeight: 400, objectFit: 'contain', display: 'block' }}
                  />
                ) : isPdf(doc.file_name) ? (
                  <Box
                    component="iframe"
                    src={presignedUrls[doc.id]}
                    sx={{ width: '100%', height: 450, border: 'none', display: 'block' }}
                    title={doc.file_name ?? 'Document'}
                  />
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Preview not available. Use "Open" to view in a new tab.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ── Manager view ──────────────────────────────────────────────────────────────
// Full reservation details with approve / reject actions.
function ManagerReservationDialog({
  open,
  slot,
  onClose,
  onSuccess,
}: {
  open: boolean;
  slot: BlockedSlot;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { community } = useUser();
  const queryClient = useQueryClient();
  const [rejectReason, setRejectReason] = React.useState('');
  const [showRejectForm, setShowRejectForm] = React.useState(false);

  React.useEffect(() => {
    setRejectReason('');
    setShowRejectForm(false);
  }, [slot?.id]);

  const reservationLookupId = slot?.uuid ?? String(slot?.id);

  const { data: fullReservation, isLoading } = useQuery<AmenityReservation>({
    queryKey: ['reservation', reservationLookupId, community?.id],
    queryFn: () => getCommunityReservation(community?.id ?? '', reservationLookupId, { include: 'user,amenity,unit,documents' }),
    enabled: open && !!reservationLookupId && !!community?.id,
  });

  const res: any = fullReservation ?? slot;

  const approveMutation = useMutation({
    mutationFn: () => approveReservation(community?.id ?? '', res?.data?.id ?? res?.uuid ?? String(res?.id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-slots-all'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      toast.success('Reservation approved successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to approve reservation');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      rejectReservation(community?.id ?? '', res?.data?.id ?? res?.uuid ?? String(res?.id), {
        rejected_reason: rejectReason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-slots-all'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      toast.success('Reservation rejected successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to reject reservation');
    },
  });

  const handleClose = () => {
    setRejectReason('');
    setShowRejectForm(false);
    onClose();
  };

  const status: string = res?.data?.status ?? res?.status ?? '';
  const isPending = status === 'pending';
  const isRejected = status === 'rejected';
  const isBusy = approveMutation.isPending || rejectMutation.isPending;

  const startLocal = dayjs.utc(res?.data?.start_time ?? res?.start_time).local();
  const endLocal = dayjs.utc(res?.data?.end_time ?? res?.end_time).local();
  const user = res?.data?.user ?? res?.user;
  const unit = res?.data?.unit ?? res?.unit;
  const amenityName = res?.data?.amenity?.name ?? res?.amenity?.name ?? res?.amenity_name ?? 'Reservation';
  const resId = String(res?.data?.id ?? res?.id ?? '');

  return (
    <ProfessionalDialog
      icon={<BankIcon size={24} weight="duotone" />}
      maxWidth="md"
      onClose={handleClose}
      open={open}
      title={amenityName}
      subtitle={`#${resId.substring(0, 8).toUpperCase()} · ${status.replace('_', ' ').toUpperCase()}`}
      actions={
        <>
          <Button onClick={handleClose} disabled={isBusy} variant="outlined">
            Close
          </Button>

          {isPending && !showRejectForm && (
            <>
              <Button
                onClick={() => setShowRejectForm(true)}
                color="error"
                variant="outlined"
                disabled={isBusy}
                startIcon={<XIcon size={18} />}
              >
                Reject
              </Button>
              <Button
                onClick={() => approveMutation.mutate()}
                color="success"
                variant="contained"
                disabled={isBusy}
                startIcon={<CheckIcon size={18} />}
              >
                {approveMutation.isPending ? 'Approving…' : 'Approve'}
              </Button>
            </>
          )}

          {showRejectForm && (
            <>
              <Button onClick={() => setShowRejectForm(false)} variant="outlined" disabled={isBusy}>
                Back
              </Button>
              <Button
                onClick={() => rejectMutation.mutate()}
                color="error"
                variant="contained"
                disabled={isBusy || !rejectReason.trim()}
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Confirm Reject'}
              </Button>
            </>
          )}
        </>
      }
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {/* Status */}
          <Chip
            label={status.replace('_', ' ').toUpperCase()}
            size="medium"
            color={statusColors[status] ?? 'default'}
            sx={{ alignSelf: 'flex-start' }}
          />

          {/* User */}
          {user && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <UserIcon size={22} weight="duotone" />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                  {unit && (
                    <Typography variant="caption" color="text.secondary">
                      Unit {unit.name}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Time */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ClockIcon size={22} weight="duotone" />
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>Reservation Time</Typography>
                <Typography variant="body2">
                  {startLocal.format('MMM D, YYYY h:mm A')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  to {endLocal.format('h:mm A')}
                </Typography>
              </Box>
            </Box>
            {(res?.data?.guest_count ?? res?.guest_count) != null && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Guests:</strong> {res?.data?.guest_count ?? res?.guest_count}
                </Typography>
              </Box>
            )}
            {(res?.data?.alcohol_served ?? res?.alcohol_served) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Alcohol will be served
              </Typography>
            )}
          </Box>

          {/* Payment */}
          {((res?.data?.deposit_amount_charged ?? res?.deposit_amount_charged) ||
            (res?.data?.total_fee_amount_charged ?? res?.total_fee_amount_charged)) && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <CurrencyDollarIcon size={20} weight="duotone" />
                <Typography variant="subtitle2" fontWeight={600}>Payment</Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Stack spacing={1.5}>
                {(res?.data?.deposit_amount_charged ?? res?.deposit_amount_charged) && (() => {
                  const r = res?.data ?? res;
                  const depositAmt = Number(r.deposit_amount_charged);
                  const refundedAt = r.deposit_refunded_at;
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Deposit</Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={refundedAt ? { textDecoration: 'line-through', color: 'text.disabled' } : {}}
                        >
                          ${depositAmt.toFixed(2)}
                        </Typography>
                        {refundedAt ? (
                          <Chip label="Refunded" size="small" color="info" />
                        ) : r.deposit_collected ? (
                          <Chip label="Paid" size="small" color="success" />
                        ) : (
                          <Chip label="Not paid" size="small" variant="outlined" />
                        )}
                      </Stack>
                    </Box>
                  );
                })()}
                {(res?.data?.total_fee_amount_charged ?? res?.total_fee_amount_charged) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Total fee</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" fontWeight={600}>
                        ${Number(res?.data?.total_fee_amount_charged ?? res?.total_fee_amount_charged).toFixed(2)}
                      </Typography>
                      {(res?.data?.fee_collected ?? res?.fee_collected) && (
                        <Chip label="Paid" size="small" color="success" />
                      )}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Purpose */}
          {(res?.data?.purpose ?? res?.purpose) && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Purpose</Typography>
              <Typography variant="body2">{res?.data?.purpose ?? res?.purpose}</Typography>
            </Box>
          )}

          {/* Special Requests */}
          {(res?.data?.special_requests ?? res?.special_requests) && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Special Requests</Typography>
              <Typography variant="body2">{res?.data?.special_requests ?? res?.special_requests}</Typography>
            </Box>
          )}

          {/* Documents */}
          {((res?.data?.documents ?? res?.documents) as AmenityReservation['documents'])?.length ? (
            <DialogDocumentsSection documents={(res?.data?.documents ?? res?.documents) as AmenityReservation['documents']} />
          ) : null}

          {/* Rejection Reason */}
          {isRejected && (res?.data?.rejected_reason ?? res?.rejected_reason) && (
            <Box sx={{ border: '1px solid', borderColor: 'error.main', borderRadius: 2, p: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={600} color="error" gutterBottom>
                Rejection Reason
              </Typography>
              <Typography variant="body2">{res?.data?.rejected_reason ?? res?.rejected_reason}</Typography>
            </Box>
          )}

          {/* Reject form */}
          {showRejectForm && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Reject Reservation</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide a reason for rejecting this reservation.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Rejection Reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this reservation is being rejected…"
              />
            </Box>
          )}
        </Stack>
      )}
    </ProfessionalDialog>
  );
}

// ── Public export ─────────────────────────────────────────────────────────────
export function ReservationDetailsDialog({
  open,
  reservation,
  onClose,
  onSuccess,
}: ReservationDetailsDialogProps): React.JSX.Element {
  const { user, community } = useUser();

  const isManager = React.useMemo(() => {
    if (!user || !community) return false;
    const accountRoles = user.account_roles ?? [];
    const hasAccountRole = accountRoles.some((role) =>
      ['account_owner', 'account_admin', 'account_manager'].includes(role.name),
    );
    const communityRoles = community.roles ?? [];
    return hasAccountRole
      || communityRoles.includes('community_manager')
      || communityRoles.includes('board_member');
  }, [user, community]);

  const isCreator = React.useMemo(() => {
    if (!user || !reservation) return false;
    const reservationUserId = (reservation as any)?.user_id ?? (reservation as any)?.data?.user_id;
    return user.id === reservationUserId;
  }, [user, reservation]);

  const isAuthorized = isManager || isCreator;

  if (!reservation) return <></>;

  if (isAuthorized) {
    return (
      <ManagerReservationDialog
        open={open}
        slot={reservation}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <ResidentSlotDialog
      open={open}
      slot={reservation}
      onClose={onClose}
    />
  );
}
